import { TextField, Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Card, CardContent, Chip, IconButton, LinearProgress, Stack, Typography, Link } from "@mui/material";
import { blue } from "@mui/material/colors";
import { FormEvent, SyntheticEvent, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import PermDeviceInformationIcon from '@mui/icons-material/PermDeviceInformation';
import { RetractedAuthor } from "./CommonInterfaces";
import RetractionPanel from "./RetractionPanel";
import { scrollToSection } from "../ScrollUtils";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint, argosWebsiteBaseLink } from "../GlobalConfig";
import { useNavigate } from 'react-router-dom';
import ShareLink from "./share/ShareLink";


interface AuthorSearchProps {
  authorSearchRequested: string | null
}

function AuthorSearch({ authorSearchRequested }: AuthorSearchProps) {

  const [inputValue, setInputValue] = useState('');
  const [validOptionSelected, setValidOptionSelected] = useState(false)
  const [possibleValues, setPossibleValues] = useState([''])
  const [retractedAuthors, setRetractedAuthors] = useState<RetractedAuthor[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResultsAvailable, setSearchResultsAvailable] = useState(false)
  const [searchInProgress, setSearchInProgress] = useState(false)
  const [inspireMe, setInspireMe] = useState(false);
  const [showError, setShowError] = useState(false)

  async function updateInput(e: SyntheticEvent, data: string) {
    setInputValue(data)
    setValidOptionSelected(false)
    if (data && data.length > 3) {

      const response = await window.fetch(argosApiEntryPoint + '/retraction/author/suggest/' + encodeURIComponent(data), {
        method: 'GET',
        headers: {
          'content-type': 'application/json;charset=UTF-8',
        }
      })

      if (response.ok) {
        const d = await response.json()
        const suggestedNames = d

        setPossibleValues(suggestedNames)
      } else {
        setPossibleValues([])
      }
    } else {
      setValidOptionSelected(false)
    }
  }

  function handleClose() {
    setSearchResultsAvailable(false)
  }

  async function authorSelected(e: SyntheticEvent, value: string | null) {
    if (value != null) {
      setValidOptionSelected(true)
      setSearchQuery(value)
      setRetractedAuthors([])
      searchRetractions(value)
    }
  }

  async function searchRetractions(searchQuery: string) {
    setSearchResultsAvailable(false)
    setSearchInProgress(true)
    setShowError(false)

    let authToken = await auth.currentUser?.getIdToken(true)

    try {
      const response = await
        fetch(argosApiEntryPoint + '/retraction?search=name:' + searchQuery, {

          method: 'GET',
          headers: {
            'content-type': 'application/json;charset=UTF-8',
            'Authorization': 'Bearer ' + authToken
          }
        });

      if (response.ok) {
        const retractedAuthors: RetractedAuthor[] = await response.json();
        setRetractedAuthors(retractedAuthors)
        setSearchInProgress(false)
        setSearchResultsAvailable(true)
      } else {
        setValidOptionSelected(false)
        setSearchQuery("")
        setRetractedAuthors([])
        setSearchInProgress(false)
        setShowError(true)
      }
    } catch (error: unknown) {
      setValidOptionSelected(false)
      setSearchQuery("")
      setRetractedAuthors([])
      setSearchInProgress(false)
      setShowError(true)
    }
  }
  useEffect(() => {
    if (authorSearchRequested !== null && authorSearchRequested.length > 3 && searchResultsAvailable === false) {
      setValidOptionSelected(true)
      setSearchQuery(authorSearchRequested)
      setRetractedAuthors([])
      searchRetractions(authorSearchRequested)
    }
  }, [authorSearchRequested]);

  useEffect(() => {
    if (searchResultsAvailable === true) {
      scrollToSection("author-search-result");
    }
  }, [searchResultsAvailable]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    console.log('Selected value: ' + inputValue);
    if (inputValue && inputValue.length > 3) {
      setSearchQuery(inputValue)
      setRetractedAuthors([])
      setPossibleValues([''])
      searchRetractions(inputValue)
    }
  }
  return (
    <>
      <div id="author-analysis" />
      <Typography gutterBottom variant="h3" component="div" sx={{color: '#003366', fontWeight: 'bold'}}>
        Author analysis
      </Typography>
      <Autocomplete
        inputValue={inputValue}
        onInputChange={(e, data) => updateInput(e, data)}
        onChange={(e, value) => authorSelected(e, value)}
        freeSolo
        selectOnFocus
        clearOnBlur
        id="author-explorer"
        options={possibleValues}
        getOptionLabel={(option) => option}
        renderInput={(params) => (
          <Card variant="outlined" >
            <CardContent>
              <form id="login" noValidate onSubmit={handleSubmit}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4 }}>
                  <TextField {...params} label="Search retractions by author name" variant="outlined"
                    helperText="Start writing the author's first name or last name and we will show matching retracted authors after 4 characters" />
                  <IconButton type="submit" aria-label="search" sx={{ ml: 2 }}>
                    <SearchIcon sx={{ color: blue, fontSize: 40 }} />
                  </IconButton>
                </Stack>
              </form>
              {!inspireMe &&
                <Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
                  <Link onClick={() => setInspireMe(true)}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>Looking for inspiration?</Typography>
                  </Link>
                </Box>}
              {inspireMe &&
                <Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>Looking for inspiration? You can try the following authors:</Typography>
                  <Link onClick={() => setInputValue('Shamshirband')}><Typography variant="caption" component="div">Shamshirband</Typography></Link>
                  <Link onClick={() => setInputValue('Jose L Calvo-Guirado')}><Typography variant="caption" component="div">Jose L Calvo-Guirado</Typography></Link>
                  <Link onClick={() => setInputValue('Vladimir Anatolevich Parfenov')}><Typography variant="caption" component="div">Vladimir Anatolevich Parfenov</Typography></Link>
                </Box>}
            </CardContent>
          </Card>
        )}
        open={inputValue.length > 3 && !validOptionSelected}
      />
      {searchInProgress &&
        <LinearProgress />}

      {showError &&
        <Card variant="outlined" sx={{ pt: 2, mt: 2 }} >
          <Stack direction="row">
            <Box flexGrow={1}>
              <Stack direction="row"><PermDeviceInformationIcon color="error" sx={{ ml: 4 }} />
                <Typography gutterBottom variant="h6" component="div" sx={{ ml: 2, mb: 2 }} color="error">
                  Sorry, there was an error trying to run your search
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Card>
      }

      {searchResultsAvailable &&
        <Card variant="outlined" sx={{ pt: 2, mt: 2 }} >
          <Stack direction="row">
            <Box flexGrow={1}>
              {retractedAuthors.length > 0 &&
                <>
                  <div id="author-search-result" />
                  <Typography gutterBottom variant="caption" component="div" sx={{ ml: 2 }}>
                    Search results for {searchQuery} - Showing {retractedAuthors.length} matching authors with entries on our database
                  </Typography>
                </>
              }
              {retractedAuthors.length === 0 &&
                <Stack direction="row"><PermDeviceInformationIcon color="primary" sx={{ ml: 4 }} />
                  <Typography gutterBottom variant="h6" component="div" sx={{ ml: 2, mb: 2 }} color="primary">
                    Good news: We are not able to find any retraction, expression of concern or correction for {searchQuery}
                  </Typography>
                </Stack>
              }
            </Box>
            <Box>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'relative',
                  top: 0,
                  right: 4,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Stack>
          {retractedAuthors.map((element, index) => {

            return <CardContent sx={{ alignContent: 'initial' }} key={'author_' + index}>

              <Typography gutterBottom variant="h3" component="div">
                {element.displayName} <ShareLink textToCopy={argosWebsiteBaseLink + "?author=" + encodeURIComponent(element.displayName)} />
              </Typography>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  Alternative names ({element.displayNameAlternatives.length})
                </AccordionSummary>
                <AccordionDetails>
                  {element.displayNameAlternatives.join(", ")}
                </AccordionDetails>
              </Accordion>
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  Institutions ({element.institutions.length})
                </AccordionSummary>
                <AccordionDetails>
                  {element.institutions.join(", ")}
                </AccordionDetails>
              </Accordion>
              <Chip label={"Works count: " + element.worksCount} variant="filled" sx={{ mr: 1 }} />
              <Chip label={"Retractions count: " + element.retractedWorksCount} variant="outlined" sx={{ mr: 1 }} />
              <Chip label={"Expressions of concern count: " + element.retractedWorks.filter(element => element.retractionNature === "Expression of concern").length} variant="filled" sx={{ mr: 1 }} />
              <Chip label={"Cited by count: " + element.citedByCount} variant="outlined" sx={{ mr: 1 }} />
              <Chip label={"HIndex: " + element.hIndex} variant="filled" />
              {element.retractedWorks.sort((a, b) => (a.retractionNature > b.retractionNature ? -1 : 1)).map(
                (retraction, retractionIndex) =>
                  <RetractionPanel retraction={retraction} index={index} retractionIndex={retractionIndex} />
              )
              }
            </CardContent>
          })}
        </Card>}

    </>
  )
}

export default AuthorSearch;